import { useMemo } from 'react';
import { Select, type SelectProps } from '@mantine/core';
import { useLocations } from '../api';

interface LocationSelectProps extends Omit<SelectProps, 'data' | 'value' | 'onChange' | 'searchable'> {
  siteId: string | undefined;
  value: string;
  onChange: (locationId: string, label: string) => void;
}

/** Site-scoped location picker (from-silo/warehouse — BUILD_SPEC.md §8.4 step 1).
 * A site's locations are a short, fully-enumerable list, so this fetches the whole
 * (active) set for the chosen site once. Disabled until a site is chosen. Mirrors
 * features/stores-transfers/components/LocationSelect.tsx. */
export function LocationSelect({ siteId, value, onChange, ...rest }: LocationSelectProps) {
  const { data, isLoading } = useLocations(siteId);

  const options = useMemo(
    () => (data?.items ?? []).map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` })),
    [data],
  );

  return (
    <Select
      {...rest}
      searchable
      data={options}
      value={value || null}
      disabled={!siteId || rest.disabled}
      placeholder={!siteId ? 'Choose a site first' : (rest.placeholder ?? 'Choose a location…')}
      onChange={(newValue) => {
        const match = newValue ? options.find((o) => o.value === newValue) : undefined;
        onChange(newValue ?? '', match?.label ?? '');
      }}
      nothingFoundMessage={isLoading ? 'Loading…' : 'No locations found'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}
